/**
 * The authenticated application shell.
 *
 * Every screen behind here lives under `/org/<handle>/...`, and that handle is
 * the SOURCE OF TRUTH for which organization is open. Opening a link switches
 * you into the organization it names; the remembered selection in local
 * storage is only what decides where you land when the URL does not say.
 *
 * Also the gate for the states a signed-in user can be in before they can do
 * anything useful:
 *
 *  1. a temporary password -> pinned to /change-password
 *  2. no organization at all -> an explanatory dead end (see NoOrganization)
 *  3. a handle they are not a member of -> back to one they are
 *  4. an organization whose context is still loading -> a brief shell
 *
 * Handling all of them here means no individual screen has to defend against a
 * null organization.
 */
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from './Topbar.jsx';
import { ContextNav } from './ContextNav.jsx';
import { MobileNav } from './MobileNav.jsx';
import { ShortcutsDialog } from './ShortcutsDialog.jsx';
import { BrandMark } from './Brand.jsx';
import { ShellProvider, useCreateActions, useVisibleApps } from './ShellContext.jsx';
import { activeItemIn, appName, itemLabel, orgRelative, resolveAppKey, WORKSPACE_PAGES } from './apps.js';
import { useNavCollapsed, useWorkspacePrefs } from './workspacePrefs.js';
import { useAuth } from '../context/AuthContext.jsx';
import { TenantProvider, useTenant } from '../context/TenantContext.jsx';
import { LocaleProvider, useLocale } from '../context/LocaleContext.jsx';
import { OrgBaseProvider, orgBasePath } from '../routes/orgNavigation.jsx';
import { EmptyState, LoadingState } from '../components/States.jsx';
import { useApi } from '../hooks/useApi.js';
import { useAppearance } from '../hooks/useAppearance.js';
import { purchaseApi } from '../services/index.js';

/**
 * A signed-in account that belongs to no organization.
 *
 * Unreachable through the normal flow — every account is created either as an
 * organization's owner or as a member of one — but it happens: the last owner
 * removed them, or their organization was deleted. Previously this state
 * redirected to a create-organization form. That form is gone, because
 * organizations are created by platform staff, so the honest thing to show is
 * why they are stuck and who can unstick them.
 */
function NoOrganization({ email, onSignOut }) {
  const { t } = useTranslation('navigation');
  return (
    <div className="auth-shell auth-shell-simple">
      <div className="auth-card">
        <div className="auth-brand">
          <BrandMark size={30} />
          <span className="brand-word">
            Nex<span className="brand-word-strong">ERP</span>
          </span>
        </div>
        <EmptyState
          icon={'⚑'}
          title={t('shell.noOrganization.title')}
          message={t('shell.noOrganization.message')}
          action={
            <button type="button" className="btn" onClick={onSignOut}>
              {t('userMenu.signOut')}
            </button>
          }
        />
        <p className="auth-footer">{t('shell.noOrganization.signedInAs', { email })}</p>
      </div>
    </div>
  );
}

/**
 * The command center is only needed once someone opens it, so it is its own
 * chunk — and fetched while the browser is idle after the shell mounts, so the
 * first Ctrl K is as instant as every later one.
 */
const loadCommandCenter = () => import('./CommandCenter.jsx');
const CommandCenter = lazy(() => loadCommandCenter().then((module) => ({ default: module.CommandCenter })));

/** What a lazily-loaded screen shows while its code arrives: the page's shape. */
function PageSkeleton() {
  return (
    <div className="page-skeleton" aria-hidden="true">
      <div className="skeleton" style={{ width: 120, height: 10 }} />
      <div className="skeleton" style={{ width: 260, height: 22, marginTop: 10 }} />
      <div className="page-skeleton-card">
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div key={row} className="skeleton" style={{ width: `${88 - ((row * 13) % 30)}%` }} />
        ))}
      </div>
    </div>
  );
}

/**
 * The NexERP shell: the top bar, the current app's navigation, the page, and
 * the launcher, command center and create menu that reach across them all.
 */
function Shell() {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { tenantId, can, loading: accessLoading } = useTenant();
  const locale = useLocale();
  const { user, signOut } = useAuth();
  const { setTheme, setDensity, density } = useAppearance();
  const canApprove = can('purchase.approve');

  const [navOpen, setNavOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useNavCollapsed();
  const [pageTitle, setPageTitle] = useState(null);

  const apps = useVisibleApps();
  const createActions = useCreateActions(apps);
  const prefs = useWorkspacePrefs(user?.id && tenantId ? `${user.id}.${tenantId}` : null);

  const { pathname, search } = orgRelative(location);

  // Which app this URL belongs to. Sticky: a page listed in two apps keeps you
  // in the one you came from.
  const previousAppKey = useRef(null);
  const appKey = useMemo(() => resolveAppKey(pathname, search, previousAppKey.current), [pathname, search]);
  useEffect(() => {
    previousAppKey.current = appKey;
  }, [appKey]);
  const currentApp = apps.find((app) => app.key === appKey) ?? null;

  // A route change closes every overlay and starts the page at the top. A
  // LAYOUT effect: it must run before the new page's header reports its title
  // (child passive effects run before a parent's), or it would wipe it.
  useLayoutEffect(() => {
    setNavOpen(false);
    setLauncherOpen(false);
    document.body.style.overflow = '';
    window.scrollTo(0, 0);
    setPageTitle(null);
  }, [location.pathname]);

  // The approvals badge: a count-only query, and only for someone who can
  // actually act on it.
  const { data: approvals } = useApi(
    async () => {
      if (!canApprove) return 0;
      const result = await purchaseApi.listRequests({ status: 'SUBMITTED', limit: 1 });
      return result?.pagination?.total ?? 0;
    },
    [tenantId, canApprove],
    { initialData: 0 }
  );
  const pendingApprovals = approvals ?? 0;

  // The page's own name, as its header states it, for the tab title and for
  // "Jump back in". Until a header reports one, a page that IS a navigation
  // destination goes by that destination's name; a detail page waits for its
  // header, so a record is never remembered as "Purchase orders".
  const navItem = useMemo(() => {
    const items = currentApp ? currentApp.items : WORKSPACE_PAGES;
    const item = activeItemIn(items, pathname, search);
    return item && item.to.split('?')[0] === pathname ? item : null;
  }, [currentApp, pathname, search]);
  // An app's own home is called by the app's name, not "Overview". In the
  // interface language, once (the tab title is not read in the moment).
  const plain = { bilingual: false };
  const atAppHome = Boolean(currentApp) && [currentApp.home, currentApp.homeTo].includes(pathname);
  const label = pageTitle ?? (atAppHome ? appName(currentApp, t, plain) : navItem ? itemLabel(navItem, t, plain) : null) ?? null;
  const currentAppName = currentApp ? appName(currentApp, t, plain) : null;

  useEffect(() => {
    const parts = [label, currentAppName, 'NexERP'].filter(Boolean);
    document.title = parts.filter((part, index) => parts.indexOf(part) === index).join(' · ');
  }, [label, currentAppName]);

  const { recordVisit } = prefs;
  useEffect(() => {
    if (!label || pathname === '/') return;
    recordVisit({ to: pathname, label, app: currentApp?.key ?? null });
  }, [label, pathname, currentApp, recordVisit]);

  // --- keyboard ---------------------------------------------------------------

  const toggleNavCollapsed = useCallback(() => setNavCollapsed(!navCollapsed), [navCollapsed, setNavCollapsed]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'k') {
        // Never over an open dialog: every Modal listens for Escape in the
        // capture phase, so the Escape that closed the palette would close
        // the dialog beneath it too — and with it a half-filled form.
        if (document.querySelector('.modal-backdrop')) return;
        event.preventDefault();
        setLauncherOpen(false);
        setCommandOpen((open) => !open);
        return;
      }
      if (event.defaultPrevented || mod || event.altKey) return;
      const target = event.target;
      if (target?.closest?.('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;

      if (event.key === '/') {
        event.preventDefault();
        setCommandOpen(true);
      } else if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
      } else if (event.key === '[' && currentApp) {
        event.preventDefault();
        toggleNavCollapsed();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentApp, toggleNavCollapsed]);

  // Warm the on-demand pieces of the shell once the page has settled.
  useEffect(() => {
    const idle = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 1500));
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = idle(() => {
      loadCommandCenter().catch(() => {});
      import('./AppLauncher.jsx').catch(() => {});
    });
    return () => cancel(handle);
  }, []);

  // The page eases in on every route change, without remounting anything.
  const pageRef = useRef(null);
  useEffect(() => {
    const node = pageRef.current;
    if (!node) return;
    node.classList.remove('page-enter');
    void node.offsetWidth;
    node.classList.add('page-enter');
  }, [location.pathname]);

  // Commands the command center offers besides pages and records: the same
  // preferences and actions the account menu has. Shown in the interface
  // language; the English label (altLabel) and keywords still find them.
  const commands = useMemo(() => {
    const densityKey = density === 'compact' ? 'comfortable' : 'compact';
    const list = [
      { id: 'theme-light', key: 'themeLight', label: 'Switch to light theme', keywords: 'appearance mode', icon: 'sun', hint: 'preferences', run: () => setTheme('light') },
      { id: 'theme-dark', key: 'themeDark', label: 'Switch to dark theme', keywords: 'appearance mode night', icon: 'moon', hint: 'preferences', run: () => setTheme('dark') },
      { id: 'theme-system', key: 'themeSystem', label: 'Use the system theme', keywords: 'appearance mode auto', icon: 'monitor', hint: 'preferences', run: () => setTheme('system') },
      {
        id: 'density',
        key: densityKey === 'comfortable' ? 'densityComfortable' : 'densityCompact',
        label: density === 'compact' ? 'Use comfortable tables' : 'Use compact tables',
        keywords: 'density rows spacing',
        icon: 'density',
        hint: 'preferences',
        run: () => setDensity(densityKey),
      },
      { id: 'shortcuts', key: 'shortcuts', label: 'Keyboard shortcuts', keywords: 'help keys', icon: 'keyboard', hint: 'help', run: () => setShortcutsOpen(true) },
      { id: 'sign-out', key: 'signOut', label: 'Sign out', keywords: 'log out logout exit', icon: 'log-out', hint: 'account', run: signOut },
    ];
    return list.map(({ key, label: english, hint, ...command }) => ({
      ...command,
      label: t(`commands.${key}`, { defaultValue: english }),
      altLabel: english,
      hint: t(`commands.hints.${hint}`),
    }));
  }, [density, setDensity, setTheme, signOut, t]);

  // The app as screens show it: Layout.jsx's breadcrumb leads with
  // `currentApp.name`, so the shell hands screens the name in the interface
  // language (the English one stays available as `englishName`).
  const shellApp = useMemo(
    () => (currentApp ? { ...currentApp, englishName: currentApp.name, name: appName(currentApp, t) } : null),
    [currentApp, t]
  );

  const shell = useMemo(
    () => ({
      apps,
      currentApp: shellApp,
      createActions,
      prefs,
      pendingApprovals,
      openCommand: () => setCommandOpen(true),
      openLauncher: () => setLauncherOpen(true),
      setPageTitle,
    }),
    [apps, shellApp, createActions, prefs, pendingApprovals]
  );

  const showNav = Boolean(currentApp);

  return (
    <ShellProvider value={shell}>
      <a href="#main" className="skip-link">
        {t('shell.skipToContent')}
      </a>
      <div className={`shell ${showNav ? 'shell-has-nav' : 'shell-workspace'} ${navCollapsed ? 'shell-nav-collapsed' : ''}`}>
        <Topbar
          apps={apps}
          currentApp={currentApp}
          launcherOpen={launcherOpen}
          onLauncherOpenChange={setLauncherOpen}
          onOpenCommand={() => {
            setLauncherOpen(false);
            setCommandOpen(true);
          }}
          onOpenNav={() => setNavOpen(true)}
          createActions={createActions}
          pendingApprovals={pendingApprovals}
          pinned={prefs.pinned}
          recent={prefs.recent}
          loading={accessLoading}
          onShowShortcuts={() => setShortcutsOpen(true)}
        />

        <div className="shell-body">
          {showNav && navOpen && <div className="ctxnav-scrim" onClick={() => setNavOpen(false)} role="presentation" />}
          {showNav && (
            <ContextNav
              app={currentApp}
              collapsed={navCollapsed}
              onToggleCollapsed={toggleNavCollapsed}
              open={navOpen}
              onNavigate={() => setNavOpen(false)}
              pendingApprovals={pendingApprovals}
              loading={accessLoading}
            />
          )}

          <div className="main">
            <main className="page page-enter" id="main" ref={pageRef} tabIndex={-1}>
              {/* Here rather than around the whole router: a screen whose code
                  is still downloading shows its skeleton INSIDE the shell,
                  instead of replacing the shell with a spinner. */}
              <Suspense fallback={<PageSkeleton />}>
                {/* Held back until the organization's language and formats
                    are known (instant when remembered from the last visit),
                    and keyed on the formatting revision: a change of
                    language, timezone or format re-renders the open screen
                    in the new form, including text that is only formatted
                    and never subscribes to anything. */}
                {locale.ready ? <Outlet key={locale.revision} /> : <PageSkeleton />}
              </Suspense>
            </main>
          </div>
        </div>

        <MobileNav
          currentApp={currentApp}
          createActions={createActions}
          onOpenLauncher={() => setLauncherOpen(true)}
          onOpenCommand={() => setCommandOpen(true)}
          onOpenNav={() => setNavOpen(true)}
        />
      </div>

      {commandOpen && (
        <Suspense fallback={null}>
          <CommandCenter
            open={commandOpen}
            onClose={() => setCommandOpen(false)}
            apps={apps}
            createActions={createActions}
            pinned={prefs.pinned}
            recent={prefs.recent.filter((entry) => entry.to !== pathname)}
            commands={commands}
          />
        </Suspense>
      )}
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </ShellProvider>
  );
}

/**
 * Everything a signed-in user must satisfy before an organization's screens
 * may render, in the order the answers become available.
 *
 * Returns an element to render INSTEAD of the shell, or null to carry on.
 * Shared with the entry redirect below, so "you are signed out" and "you still
 * hold a temporary password" cannot come to be answered differently in the two
 * places that ask.
 */
function useAdmissionGate() {
  const { t } = useTranslation('navigation');
  const { status, isAuthenticated, hasOrganization, mustChangePassword, user, signOut } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingState label={t('shell.restoringSession')} />;

  if (!isAuthenticated) {
    // Remember where they were headed, so sign-in can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Still on a temporary password. This redirect is a courtesy — their token
  // is scoped to the password-change endpoints, so every screen behind here
  // would fail its own requests anyway.
  if (mustChangePassword) return <Navigate to="/change-password" replace />;

  if (!hasOrganization) return <NoOrganization email={user?.email} onSignOut={signOut} />;

  return null;
}

/**
 * The entry point, and the bridge from the old URLs.
 *
 * Mounted on `/` and on everything that matches nothing else. It sends a
 * signed-in user into an organization and CARRIES THE REST OF THE PATH WITH
 * THEM, so a bookmark or an emailed link to `/purchase-orders/<id>` still
 * arrives at the screen it always did rather than at a 404.
 *
 * Which organization: the one they last had open, falling back to the first
 * they belong to.
 */
export function OrganizationEntry() {
  const { t } = useTranslation('navigation');
  const { organizations, tenantId } = useAuth();
  const location = useLocation();

  const blocked = useAdmissionGate();
  if (blocked) return blocked;

  const active = organizations.find((organization) => organization.id === tenantId) ?? organizations[0];
  if (!active) return <LoadingState label={t('shell.openingOrganization')} />;

  // '/' and a bare '/org' carry nothing worth keeping; anything else is a path
  // that used to work and should still land where it used to.
  const trailing = location.pathname === '/' || location.pathname === '/org' ? '' : location.pathname;

  return <Navigate to={`${orgBasePath(active.slug)}${trailing}${location.search}`} replace />;
}

export function AppLayout() {
  const { t } = useTranslation('navigation');
  const { organizations, tenantId, selectOrganization } = useAuth();
  const { orgSlug } = useParams();

  const organization = organizations.find((candidate) => candidate.slug === orgSlug);
  const opened = organization?.id ?? null;

  // The URL decides, so a link into another of your organizations switches you
  // into it rather than showing it to you under the wrong context. Persisting
  // the choice is what makes '/' bring you back here next time.
  useEffect(() => {
    if (opened && opened !== tenantId) selectOrganization(opened);
  }, [opened, tenantId, selectOrganization]);

  const blocked = useAdmissionGate();
  if (blocked) return blocked;

  // A handle that is not theirs — mistyped, revoked, or belonging to somebody
  // else's organization. Both get the same answer: go where you can go. The
  // server would refuse the data regardless; this just avoids a screenful of
  // 403s saying so.
  if (!organization) return <Navigate to="/" replace />;

  // The effect above has not committed yet. One render, and it stops the shell
  // mounting against the organization that was open a moment ago.
  if (opened !== tenantId) return <LoadingState label={t('shell.openingOrganization')} />;

  return (
    <OrgBaseProvider base={orgBasePath(organization.slug)}>
      {/* Keyed on the organization, so switching remounts every data context
          rather than leaving one screen showing the previous organization. */}
      <TenantProvider key={tenantId}>
        <LocaleProvider>
          <Shell />
        </LocaleProvider>
      </TenantProvider>
    </OrgBaseProvider>
  );
}
